import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Slider,
  CircularProgress,
} from '@mui/material';
import { CropFree, ZoomIn, ZoomOut } from '@mui/icons-material';

interface Point {
  x: number;
  y: number;
}

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

type AspectRatioOption = '1:1' | '9:16';

interface ImageCropperProps {
  image: string;
  open: boolean;
  onClose: () => void;
  onCropComplete: (croppedBlob: Blob) => void;
  loading?: boolean;
}

const ASPECT_RATIOS: Record<AspectRatioOption, { value: number; label: string; description: string }> = {
  '1:1': { value: 1, label: 'Feed (1:1)', description: '1080×1080' },
  '9:16': { value: 9 / 16, label: 'Stories (9:16)', description: '1080×1920' },
};

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.1;

export default function ImageCropper({
  image,
  open,
  onClose,
  onCropComplete,
  loading = false,
}: ImageCropperProps) {
  const [aspectRatio, setAspectRatio] = useState<AspectRatioOption>('1:1');
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState<Point>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Point>({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Reset state when image changes
  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, [image]);

  // Load image to get natural dimensions
  useEffect(() => {
    if (image) {
      const img = new Image();
      img.onload = () => {
        setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.src = image;
    }
  }, [image]);

  const handleAspectRatioChange = (_: React.MouseEvent<HTMLElement>, newRatio: AspectRatioOption | null) => {
    if (newRatio) {
      setAspectRatio(newRatio);
      // Reset position and recalculate zoom when changing aspect ratio
      setPosition({ x: 0, y: 0 });
      // Zoom will be constrained on next render via effect
    }
  };

  // Define getCropArea first as other callbacks depend on it
  const getCropArea = useCallback((): Area => {
    if (!containerRef.current) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    const ratio = ASPECT_RATIOS[aspectRatio].value;
    let cropWidth: number;
    let cropHeight: number;

    // Calculate crop area to fill the container (max width or height)
    if (containerWidth / containerHeight > ratio) {
      // Container is wider than crop ratio - use full height
      cropHeight = containerHeight;
      cropWidth = cropHeight * ratio;
    } else {
      // Container is taller than crop ratio - use full width
      cropWidth = containerWidth;
      cropHeight = cropWidth / ratio;
    }

    return {
      x: (containerWidth - cropWidth) / 2,
      y: (containerHeight - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight,
    };
  }, [aspectRatio]);

  // Calculate minimum zoom to cover the crop area
  const getMinZoom = useCallback((): number => {
    if (!containerRef.current || imageSize.width === 0 || imageSize.height === 0) {
      return MIN_ZOOM;
    }

    const cropArea = getCropArea();
    const minZoomX = cropArea.width / imageSize.width;
    const minZoomY = cropArea.height / imageSize.height;

    return Math.max(minZoomX, minZoomY, MIN_ZOOM);
  }, [getCropArea, imageSize]);

  // Constrain position to keep image within crop bounds
  const constrainPosition = useCallback((pos: Point, currentZoom: number): Point => {
    if (!containerRef.current || imageSize.width === 0) {
      return pos;
    }

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    const cropArea = getCropArea();
    const displayedWidth = imageSize.width * currentZoom;
    const displayedHeight = imageSize.height * currentZoom;

    // Calculate max allowed offset in each direction
    // Image left edge must be <= crop left edge
    const maxX = cropArea.x - containerWidth / 2 + displayedWidth / 2;
    // Image right edge must be >= crop right edge
    const minX = cropArea.x + cropArea.width - containerWidth / 2 - displayedWidth / 2;

    // Image top edge must be <= crop top edge
    const maxY = cropArea.y - containerHeight / 2 + displayedHeight / 2;
    // Image bottom edge must be >= crop bottom edge
    const minY = cropArea.y + cropArea.height - containerHeight / 2 - displayedHeight / 2;

    return {
      x: Math.min(maxX, Math.max(minX, pos.x)),
      y: Math.min(maxY, Math.max(minY, pos.y)),
    };
  }, [getCropArea, imageSize]);

  // Apply minimum zoom and constraints when image size or aspect ratio changes
  useEffect(() => {
    if (imageSize.width > 0 && containerRef.current) {
      const minZoom = getMinZoom();
      setZoom((prev) => {
        const newZoom = Math.max(minZoom, prev);
        // Also constrain position with the new zoom
        setPosition((pos) => constrainPosition(pos, newZoom));
        return newZoom;
      });
    }
  }, [imageSize, aspectRatio, getMinZoom, constrainPosition]);

  // Scroll to zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    const minZoom = getMinZoom();
    setZoom((prev) => {
      const newZoom = Math.min(MAX_ZOOM, Math.max(minZoom, prev + delta));
      setPosition((pos) => constrainPosition(pos, newZoom));
      return newZoom;
    });
  }, [getMinZoom, constrainPosition]);

  const handleZoomChange = (_: Event, newValue: number | number[]) => {
    const minZoom = getMinZoom();
    const newZoom = Math.max(minZoom, newValue as number);
    setZoom(newZoom);
    setPosition((pos) => constrainPosition(pos, newZoom));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const newPos = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    };
    setPosition(constrainPosition(newPos, zoom));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || e.touches.length !== 1) return;
    const newPos = {
      x: e.touches[0].clientX - dragStart.x,
      y: e.touches[0].clientY - dragStart.y,
    };
    setPosition(constrainPosition(newPos, zoom));
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleCrop = useCallback(async () => {
    if (!imageRef.current || !containerRef.current) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = imageRef.current;
    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const cropArea = getCropArea();

    // Calculate the scale between displayed image and natural image
    const displayedWidth = imageSize.width * zoom;
    const displayedHeight = imageSize.height * zoom;

    // Image position within container (centered + user offset)
    const imageX = (containerRect.width - displayedWidth) / 2 + position.x;
    const imageY = (containerRect.height - displayedHeight) / 2 + position.y;

    // Calculate what part of the original image is in the crop area
    const scaleX = imageSize.width / displayedWidth;
    const scaleY = imageSize.height / displayedHeight;

    const sourceX = (cropArea.x - imageX) * scaleX;
    const sourceY = (cropArea.y - imageY) * scaleY;
    const sourceWidth = cropArea.width * scaleX;
    const sourceHeight = cropArea.height * scaleY;

    // Set canvas size based on aspect ratio (using standard FB sizes)
    if (aspectRatio === '1:1') {
      canvas.width = 1080;
      canvas.height = 1080;
    } else {
      canvas.width = 1080;
      canvas.height = 1920;
    }

    // Draw the cropped image
    ctx.drawImage(
      img,
      Math.max(0, sourceX),
      Math.max(0, sourceY),
      Math.min(imageSize.width - sourceX, sourceWidth),
      Math.min(imageSize.height - sourceY, sourceHeight),
      0,
      0,
      canvas.width,
      canvas.height
    );

    // Convert to blob
    canvas.toBlob(
      (blob) => {
        if (blob) {
          onCropComplete(blob);
        }
      },
      'image/jpeg',
      0.9
    );
  }, [aspectRatio, zoom, position, imageSize, getCropArea, onCropComplete]);

  const cropArea = getCropArea();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Crop Image</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Placement
          </Typography>
          <ToggleButtonGroup
            value={aspectRatio}
            exclusive
            onChange={handleAspectRatioChange}
            size="small"
          >
            {Object.entries(ASPECT_RATIOS).map(([key, { label, description }]) => (
              <ToggleButton key={key} value={key}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2">{label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {description}
                  </Typography>
                </Box>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box
          ref={containerRef}
          sx={{
            position: 'relative',
            width: '100%',
            height: 400,
            bgcolor: 'grey.900',
            overflow: 'hidden',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
        >
          {/* Image */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px)) scale(${zoom})`,
              transformOrigin: 'center',
            }}
          >
            <img
              ref={imageRef}
              src={image}
              alt="Crop preview"
              style={{
                maxWidth: 'none',
                maxHeight: 'none',
                display: 'block',
              }}
              draggable={false}
            />
          </Box>

          {/* Overlay with crop window */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
            }}
          >
            {/* Dark overlay */}
            <svg width="100%" height="100%">
              <defs>
                <mask id="cropMask">
                  <rect width="100%" height="100%" fill="white" />
                  <rect
                    x={cropArea.x}
                    y={cropArea.y}
                    width={cropArea.width}
                    height={cropArea.height}
                    fill="black"
                  />
                </mask>
              </defs>
              <rect
                width="100%"
                height="100%"
                fill="rgba(0,0,0,0.6)"
                mask="url(#cropMask)"
              />
              {/* Crop border */}
              <rect
                x={cropArea.x}
                y={cropArea.y}
                width={cropArea.width}
                height={cropArea.height}
                fill="none"
                stroke="white"
                strokeWidth="2"
              />
              {/* Grid lines */}
              <line
                x1={cropArea.x + cropArea.width / 3}
                y1={cropArea.y}
                x2={cropArea.x + cropArea.width / 3}
                y2={cropArea.y + cropArea.height}
                stroke="rgba(255,255,255,0.3)"
              />
              <line
                x1={cropArea.x + (cropArea.width * 2) / 3}
                y1={cropArea.y}
                x2={cropArea.x + (cropArea.width * 2) / 3}
                y2={cropArea.y + cropArea.height}
                stroke="rgba(255,255,255,0.3)"
              />
              <line
                x1={cropArea.x}
                y1={cropArea.y + cropArea.height / 3}
                x2={cropArea.x + cropArea.width}
                y2={cropArea.y + cropArea.height / 3}
                stroke="rgba(255,255,255,0.3)"
              />
              <line
                x1={cropArea.x}
                y1={cropArea.y + (cropArea.height * 2) / 3}
                x2={cropArea.x + cropArea.width}
                y2={cropArea.y + (cropArea.height * 2) / 3}
                stroke="rgba(255,255,255,0.3)"
              />
            </svg>
          </Box>
        </Box>

        {/* Zoom control */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <ZoomOut />
          <Slider
            value={zoom}
            onChange={handleZoomChange}
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={ZOOM_STEP}
            sx={{ flex: 1 }}
          />
          <ZoomIn />
          <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right' }}>
            {Math.round(zoom * 100)}%
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCrop}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <CropFree />}
        >
          {loading ? 'Uploading...' : 'Crop & Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
